import React, { useMemo, useState } from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { ClientPortalProvider, useClientPortal } from '../../context/ClientPortalContext';
import { useAuth } from '../../context/AuthContext';

const ALLOWED_ROLES = new Set(['client', 'admin', 'manager', 'agreements']);

const PortalShell = () => {
  const location = useLocation();
  const { client, loading, error, counts } = useClientPortal();
  const { signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const initials = useMemo(() => {
    if (!client) return 'RM';
    const parts = [client.first_name, client.last_name].filter(Boolean);
    if (parts.length === 0) return 'RM';
    return parts
      .map((p) => p.trim()[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }, [client]);

  const navItems = [
    { label: 'Dashboard', to: '/portal', exact: true },
    { label: 'Applications', to: '/portal/applications' },
    { label: 'Documents', to: '/portal/documents' },
    { label: 'Agreements', to: '/portal/agreements', badge: counts.agreements },
    { label: 'Payments', to: '/portal/payments' },
    { label: 'Messages', to: '/portal/messages', badge: counts.messages },
    { label: 'Credentials', to: '/portal/credentials' },
    { label: 'Profile', to: '/portal/profile' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="lg:hidden bg-white/95 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <button
          type="button"
          aria-label="Toggle navigation"
          className="p-2 text-slate-600 hover:text-slate-900 transition-colors rounded-lg hover:bg-slate-100"
          onClick={() => setSidebarOpen((prev) => !prev)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900">{client?.first_name || 'Client'}</p>
            <p className="text-xs text-slate-500">{client?.email || 'welcome@ravana-migration.com'}</p>
          </div>
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-semibold shadow-lg">
            {initials}
          </div>
        </div>
      </div>

      <div className="flex">
        <div
          className={`fixed inset-y-0 left-0 z-30 w-72 bg-white/95 backdrop-blur border-r border-slate-200 transform transition-all duration-300 ease-out lg:static lg:translate-x-0 shadow-xl lg:shadow-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
          <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-r from-indigo-500 to-purple-600 text-white -mx-1 px-6 pt-6 pb-6">
            <h1 className="text-xl font-bold">Client Portal</h1>
            <p className="text-xs text-indigo-100 mt-1">Ravana Migration</p>
          </div>
          <nav className="mt-6 space-y-1 px-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive
                    ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-bold bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full shadow-sm">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="px-6 py-4 border-t border-slate-100 mt-8">
            <button
              type="button"
              onClick={signOut}
              className="w-full text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>

        <div className="flex-1">
          <header className="hidden lg:flex items-center justify-between bg-white/80 backdrop-blur px-8 py-6 border-b border-slate-200 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-medium">Currently viewing</p>
              <h2 className="text-3xl font-bold text-slate-900 mt-1">
                {navItems.find((item) =>
                  item.exact
                    ? location.pathname === item.to
                    : location.pathname.startsWith(item.to)
                )?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-4 bg-white rounded-2xl px-4 py-2 shadow-sm border border-slate-100">
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900">{client?.first_name} {client?.last_name}</p>
                <p className="text-xs text-slate-500">{client?.email}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-semibold text-lg shadow-lg">
                {initials}
              </div>
            </div>
          </header>

          <main className="p-4 sm:p-6 lg:p-8">
            {loading && (
              <div className="bg-white/80 backdrop-blur border border-slate-200 rounded-3xl p-12 text-center shadow-sm">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mb-4"></div>
                <p className="text-slate-600 font-medium">Loading your workspace…</p>
              </div>
            )}
            {!loading && error && (
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 text-rose-700 rounded-3xl p-8 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-bold text-lg mb-1">We could not load your client record</p>
                    <p className="text-sm text-rose-600">Please contact support or try again later.</p>
                  </div>
                </div>
              </div>
            )}
            {!loading && !error && <Outlet />}
          </main>
        </div>
      </div>
    </div>
  );
};

const ClientPortalLayout = () => {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">
        Preparing secure portal…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!ALLOWED_ROLES.has(role)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <div className="max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <p className="text-sm font-semibold text-slate-900">Access restricted</p>
          <p className="text-sm text-slate-500 mt-2">
            Your account does not have client portal access yet. Please contact your Ravana Migration consultant.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ClientPortalProvider>
      <PortalShell />
    </ClientPortalProvider>
  );
};

export default ClientPortalLayout;
